import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Plus, Edit, Trash, Search } from "lucide-react";
import { getPendingCamaras, deleteCamaraById } from "../../camaraManager/api/camaraManager.api";
import PendingCamaraFormModal, { PendingCamara } from "./PendingCamaraFormModal.tsx";

const PendingCamaras = () => {
  const [users, setUsers] = useState<PendingCamara[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<PendingCamara | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await getPendingCamaras();
      const userData = data.map((item: any) => {
        let coords = item.coordinates;
        if (typeof coords === 'string' && coords.includes(',')) {
          const [lat, lng] = coords.split(',').map(Number);
          coords = { lat, lng };
        }
        return { ...item, coordinates: coords };
      }) as PendingCamara[];
      setUsers(userData.sort((a, b) => (Number(a.internalID) || 0) - (Number(b.internalID) || 0)));
    } catch (err) { toast.error("Error al cargar cámaras."); }
    setLoading(false);
  };

  const handleDelete = async (userId: string) => {
    if (!window.confirm("¿Estás seguro de que quieres eliminar esta camara?")) return;

    setLoading(true);
    try {
      await deleteCamaraById(userId);
      await fetchUsers();
      toast.success("Camara eliminado exitosamente!");
    } catch (err) {
      toast.error("Error al eliminar el camara");
      console.error(err);
    }
    setLoading(false);
  };

  const openModal = (user: PendingCamara | null = null) => {
    setEditingUser(user);
    setIsModalOpen(true);
  };

  const filteredUsers = users.filter(user =>
    user.Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.feedID?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.customerId?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Altas de Cámaras</h1>
        <div className="flex gap-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Buscar cámaras..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            />
            <Search className="absolute left-3 top-2.5 text-gray-400 w-5 h-5" />
          </div>
          <button
            onClick={() => openModal()}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Agregar nueva cámara
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Feed ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Identificador</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Días Feed</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timelapse Auto</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap font-medium">{user.Name}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{user.feedID}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{user.identifier}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{user.customerId}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      user.status === 'Aprobado' ? 'bg-green-100 text-green-800' :
                      user.status === 'Terminado' ? 'bg-gray-100 text-gray-800' :
                      user.status === 'Suspendida' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">{user.feedDays?.totalDays}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div title={user.active ? 'Genera timelapse automáticamente' : 'No genera timelapse automáticamente'} className={`w-3 h-3 rounded-full ${user.active ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => openModal(user)} className="text-blue-600 hover:text-blue-800 p-1">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => user.id && handleDelete(user.id)} className="text-red-600 hover:text-red-800 p-1">
                        <Trash className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <PendingCamaraFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        editingCamara={editingUser}
        onSaved={fetchUsers}
      />

      <ToastContainer />
    </div>
  );
};

export default PendingCamaras;
